import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import PageHeader from "@/components/PageHeader";
import { Car, Users, UserX, UserCheck, CalendarClock, MapPin, Smartphone, Cpu } from "lucide-react";
import { format, parseISO, isBefore, addDays } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { Th, Td } from "@/pages/CustomersPage";
import { safeFormatDate } from "@/lib/utils";

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    vehicles: 0,
    active_customers: 0,
    active_customer_vehicles: 0,
    inactive_customers: 0,
    inactive_customer_vehicles: 0,
  });
  const [byCity, setByCity] = useState([]);
  const [recent, setRecent] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [deviceInventory, setDeviceInventory] = useState([]);
  const [simInventory, setSimInventory] = useState([]);
  const [byEmployee, setByEmployee] = useState([]);

  // Dashboard is loaded once on mount. All referenced helpers (api, addDays,
  // parseISO, isBefore) are stable imports; setState dispatchers are stable
  // refs from useState. Intentionally one-shot.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/dashboard/by-city"),
      api.get("/dashboard/device-inventory"),
      api.get("/dashboard/sim-inventory"),
      api.get("/dashboard/installations-by-employee"),
      api.get("/vehicles"),
    ]).then(([s, bc, di, si, be, v]) => {
      setStats(s.data);
      setByCity(bc.data);
      setDeviceInventory(di.data);
      setSimInventory(si.data);
      setByEmployee(be.data);
      setRecent(v.data.slice(0, 6));
      const today = new Date();
      const horizon = addDays(today, 30);
      const upcoming = (v.data || [])
        .filter((x) => x.sim_renewal_date || x.sim_next_renewal_date)
        .map((x) => ({
          ...x,
          _date: x.sim_renewal_date || x.sim_next_renewal_date,
        }))
        .filter((x) => {
          const d = parseISO(x._date);
          return !isNaN(d) && isBefore(d, horizon);
        })
        .sort((a, b) => a._date.localeCompare(b._date))
        .slice(0, 8);
      setRenewals(upcoming);
    });
  }, []);

  const statTiles = [
    {
      label: "Total Vehicles",
      value: stats.vehicles,
      icon: Car,
      testId: "stat-vehicles",
    },
    {
      label: "Active Customers",
      value: stats.active_customers ?? stats.customers ?? 0,
      sub: `${stats.active_customer_vehicles ?? 0} vehicle(s)`,
      icon: Users,
      testId: "stat-active-customers",
    },
    {
      label: "Inactive Customers",
      value: stats.inactive_customers ?? 0,
      sub: `${stats.inactive_customer_vehicles ?? 0} vehicle(s)`,
      icon: UserX,
      testId: "stat-inactive-customers",
    },
  ];

  return (
    <div className="vsm-shell" data-testid="dashboard-page">
      <PageHeader
        overline={`${getGreeting()} · ${format(new Date(), "EEEE, dd MMMM yyyy")}`}
        title={`Welcome, ${user?.name || user?.username || "User"}`}
        description="Here is a snapshot of every SIM, device and renewal milestone tracked across the fleet."
      />

      <div className="px-4 sm:px-6 md:px-10 py-5 sm:py-8 space-y-10">
        {/* Top summary cards */}
        <section
          className="grid grid-cols-1 sm:grid-cols-3 gap-0 border vsm-border vsm-surface"
          data-testid="stats-grid"
        >
          {statTiles.map((s, i) => (
            <div
              key={s.label}
              data-testid={s.testId}
              className={`p-5 ${i !== 0 ? "sm:border-l vsm-border" : ""}`}
            >
              <div className="flex items-start justify-between">
                <div className="vsm-overline">{s.label}</div>
                <s.icon className="w-4 h-4 vsm-muted" />
              </div>
              <div
                className="text-4xl font-bold tracking-tight mt-3"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {s.value}
              </div>
              {s.sub && (
                <div className="vsm-overline mt-2" data-testid={`${s.testId}-sub`}>{s.sub}</div>
              )}
            </div>
          ))}
        </section>

        {/* Active records by location */}
        <Card className="rounded-none border vsm-border vsm-surface" data-testid="by-city-card">
          <div className="p-5 border-b vsm-border flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            <div>
              <div className="vsm-overline">Active records by location</div>
              <h2 className="text-xl font-bold tracking-tight">Customers &amp; Vehicles per Location</h2>
            </div>
          </div>
          <div className="overflow-x-auto vsm-table-scroll vsm-table-scroll" data-testid="by-city-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b vsm-border text-left">
                  <Th>Country</Th>
                  <Th>City</Th>
                  <Th>Customer ID</Th>
                  <Th>Active Customer</Th>
                  <Th className="text-right">Active Vehicles</Th>
                </tr>
              </thead>
              <tbody>
                {byCity.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 vsm-muted text-center">No active records yet.</td></tr>
                )}
                {byCity.map((r, i) => (
                  <tr key={`${r.country}-${r.city}-${r.customer_name}-${i}`} className="border-b vsm-border">
                    <Td className="font-mono uppercase text-xs">{r.country || "—"}</Td>
                    <Td>{r.city || "—"}</Td>
                    <Td className="font-mono text-xs">{r.customer_id_code || "—"}</Td>
                    <Td className="font-semibold">{r.customer_name || "—"}</Td>
                    <Td className="text-right">{r.vehicles_count ?? 0}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Upcoming Renewals + Installations by Employee (side by side) */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="rounded-none border vsm-border vsm-surface" data-testid="upcoming-renewals-card">
            <div className="p-5 border-b vsm-border">
              <div className="vsm-overline">Next 30 days</div>
              <h2 className="text-xl font-bold tracking-tight flex items-center gap-2">
                <CalendarClock className="w-5 h-5" /> Upcoming Renewals
              </h2>
              <p className="text-xs vsm-muted mt-1">SIM next renewal date within the next 30 days.</p>
            </div>
            <div className="divide-y vsm-border vsm-table-scroll" data-testid="upcoming-renewals">
              {renewals.length === 0 && (
                <div className="p-6 text-sm vsm-muted">No upcoming renewals.</div>
              )}
              {renewals.map((r) => (
                <div key={r.id} className="p-4 text-sm space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-semibold truncate">{r.vehicle_number}</div>
                    <div className="vsm-overline shrink-0">SIM Renewal</div>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs vsm-muted">
                    <div className="truncate">{r.customer_name}</div>
                    <div className="font-mono shrink-0">{safeFormatDate(r._date)}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-none border vsm-border vsm-surface" data-testid="installations-by-employee-card">
            <div className="p-5 border-b vsm-border flex items-center gap-2">
              <UserCheck className="w-5 h-5" />
              <div>
                <div className="vsm-overline">Field work</div>
                <h2 className="text-xl font-bold tracking-tight">Installations by Employee</h2>
              </div>
            </div>
            <div className="overflow-x-auto vsm-table-scroll vsm-table-scroll" data-testid="installations-by-employee-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b vsm-border text-left">
                    <Th>Country</Th>
                    <Th>City</Th>
                    <Th>Employee</Th>
                    <Th className="text-right">Vehicles</Th>
                  </tr>
                </thead>
                <tbody>
                  {byEmployee.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-8 vsm-muted text-center">No installations yet.</td></tr>
                  )}
                  {byEmployee.map((r, i) => (
                    <tr key={`e-${r.country}-${r.city}-${r.employee}-${i}`} className="border-b vsm-border">
                      <Td className="font-mono uppercase text-xs">{r.country || "—"}</Td>
                      <Td>{r.city || "—"}</Td>
                      <Td className="font-semibold">{r.employee || "—"}</Td>
                      <Td className="text-right">{r.vehicles_count ?? 0}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </section>

        {/* Recent Installations (full width, below) */}
        <Card className="rounded-none border vsm-border vsm-surface" data-testid="recent-installations-card">
          <div className="p-5 border-b vsm-border">
            <div className="vsm-overline">Activity</div>
            <h2 className="text-xl font-bold tracking-tight">Recent Installations</h2>
          </div>
          <div className="overflow-x-auto vsm-table-scroll vsm-table-scroll" data-testid="recent-installations-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b vsm-border text-left">
                  <Th>Date</Th>
                  <Th>Vehicle</Th>
                  <Th>Customer</Th>
                  <Th>Device · Provider</Th>
                  <Th className="text-right">Invoice</Th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 vsm-muted text-center">No vehicles yet.</td></tr>
                )}
                {recent.map((v) => (
                  <tr key={v.id} className="border-b vsm-border">
                    <Td className="font-mono text-xs vsm-muted">
                      {safeFormatDate(v.installation_date)}
                    </Td>
                    <Td className="font-semibold">{v.vehicle_number || "—"}</Td>
                    <Td>{v.customer_name || "—"}</Td>
                    <Td className="text-xs vsm-muted">
                      {(v.device_model_name || "—") + " · " + (v.sim_service_provider_name || "—")}
                    </Td>
                    <Td className="text-right vsm-overline">{v.invoice_type || "—"}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Device Inventory */}
        <Card className="rounded-none border vsm-border vsm-surface" data-testid="device-inventory-card">
          <div className="p-5 border-b vsm-border flex items-center gap-2">
            <Cpu className="w-5 h-5" />
            <div>
              <div className="vsm-overline">Inventory</div>
              <h2 className="text-xl font-bold tracking-tight">Device Inventory by Location</h2>
              <p className="text-xs vsm-muted mt-1">Unassigned devices only — free stock not yet installed in any vehicle.</p>
            </div>
          </div>
          <div className="overflow-x-auto vsm-table-scroll vsm-table-scroll" data-testid="device-inventory-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b vsm-border text-left">
                  <Th>Country</Th>
                  <Th>City</Th>
                  <Th>Device Model</Th>
                  <Th className="text-right">Device Count</Th>
                </tr>
              </thead>
              <tbody>
                {deviceInventory.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 vsm-muted text-center">No devices yet.</td></tr>
                )}
                {deviceInventory.map((r, i) => (
                  <tr key={`d-${r.country}-${r.city}-${r.device_model}-${i}`} className="border-b vsm-border">
                    <Td className="font-mono uppercase text-xs">{r.country || "—"}</Td>
                    <Td>{r.city || "—"}</Td>
                    <Td className="font-semibold">{r.device_model || "—"}</Td>
                    <Td className="text-right">{r.count ?? 0}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* SIM Inventory */}
        <Card className="rounded-none border vsm-border vsm-surface" data-testid="sim-inventory-card">
          <div className="p-5 border-b vsm-border flex items-center gap-2">
            <Smartphone className="w-5 h-5" />
            <div>
              <div className="vsm-overline">Inventory</div>
              <h2 className="text-xl font-bold tracking-tight">SIM Inventory by Location</h2>
              <p className="text-xs vsm-muted mt-1">Unassigned SIMs only — free stock not yet linked to any device.</p>
            </div>
          </div>
          <div className="overflow-x-auto vsm-table-scroll vsm-table-scroll" data-testid="sim-inventory-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b vsm-border text-left">
                  <Th>Country</Th>
                  <Th>City</Th>
                  <Th>Service Provider</Th>
                  <Th className="text-right">SIMs Count</Th>
                </tr>
              </thead>
              <tbody>
                {simInventory.length === 0 && (
                  <tr><td colSpan={4} className="px-4 py-8 vsm-muted text-center">No SIMs yet.</td></tr>
                )}
                {simInventory.map((r, i) => (
                  <tr key={`s-${r.country}-${r.city}-${r.service_provider}-${i}`} className="border-b vsm-border">
                    <Td className="font-mono uppercase text-xs">{r.country || "—"}</Td>
                    <Td>{r.city || "—"}</Td>
                    <Td className="font-semibold">{r.service_provider || "—"}</Td>
                    <Td className="text-right">{r.count ?? 0}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
